import React, { useState, useEffect } from 'react';
import { t } from '../../utils/i18n';
import { hasRole } from '../../utils/roleHelper';

const AttendanceDetailPage = ({ api, user }) => {
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    requested_check_in: '',
    requested_check_out: '',
    reason: ''
  });

  const isAdmin = hasRole(user, 'admin');

  useEffect(() => {
    fetchCorrections();
  }, []);

  const fetchCorrections = async () => {
    setLoading(true);
    try {
      const endpoint = isAdmin ? '/attendance_corrections' : '/attendance_corrections/mine';
      const response = await api.get(endpoint);
      setCorrections(response.attendance_corrections || []);
    } catch (error) {
      console.error('Failed to fetch corrections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        attendance_correction: {
          ...formData,
          requested_check_in: formData.date + 'T' + formData.requested_check_in + ':00',
          requested_check_out: formData.date + 'T' + formData.requested_check_out + ':00'
        }
      };

      await api.post('/attendance_corrections', payload);
      alert('Correction request submitted successfully');
      setShowModal(false);
      setFormData({ date: '', requested_check_in: '', requested_check_out: '', reason: '' });
      fetchCorrections();
    } catch (error) {
      alert('Failed to submit correction request');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.patch(`/attendance_corrections/${id}/approve`, {});
      alert('Correction approved');
      fetchCorrections();
    } catch (error) {
      alert('Failed to approve correction');
    }
  };

  const handleReject = async (id) => {
    const notes = prompt('Enter rejection reason:');
    if (!notes) return;

    try {
      await api.patch(`/attendance_corrections/${id}/reject`, { notes });
      alert('Correction rejected');
      fetchCorrections();
    } catch (error) {
      alert('Failed to reject correction');
    }
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { class: 'yellow', text: 'Pending' },
      approved: { class: 'green', text: 'Approved' },
      rejected: { class: 'red', text: 'Rejected' }
    };
    const s = statusMap[status] || statusMap.pending;
    return <span className={`status ${s.class}`}>{s.text}</span>;
  };

  return (
    <div className="contentArea">
      <div className="dFlex baseTableHd mb_24">
        <h1 className="txt28 fwBold colorBlack">Attendance Corrections</h1>
        {!isAdmin && (
          <button className="btnPrimary" onClick={() => setShowModal(true)}>
            + Request Correction
          </button>
        )}
      </div>

      <div className="baseTableWrap">
        <div className="baseTableContainer">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
          ) : (
            <table className="baseTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Personnel ID</th>
                  <th>Requested Check In</th>
                  <th>Requested Check Out</th>
                  <th>Reason</th>
                  <th>Status</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {corrections.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? "7" : "6"} style={{ textAlign: 'center', padding: '40px' }}>
                      No correction requests found
                    </td>
                  </tr>
                ) : (
                  corrections.map((correction) => (
                    <tr key={correction.id}>
                      <td>{new Date(correction.date).toLocaleDateString()}</td>
                      <td>{correction.army_personnel_id}</td>
                      <td>{formatDateTime(correction.requested_check_in)}</td>
                      <td>{formatDateTime(correction.requested_check_out)}</td>
                      <td>{correction.reason}</td>
                      <td>{getStatusBadge(correction.status)}</td>
                      {isAdmin && (
                        <td>
                          {correction.status === 'pending' && (
                            <div className="dFlex" style={{ gap: '8px' }}>
                              <button
                                className="btnPrimary"
                                style={{ padding: '4px 12px', fontSize: '12px' }}
                                onClick={() => handleApprove(correction.id)}
                              >
                                Approve
                              </button>
                              <button
                                className="btnSecondary"
                                style={{ padding: '4px 12px', fontSize: '12px' }}
                                onClick={() => handleReject(correction.id)}
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Request Attendance Correction</h5>
                <button type="button" className="close" onClick={() => setShowModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Check In Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.requested_check_in}
                      onChange={(e) => setFormData({ ...formData, requested_check_in: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Check Out Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.requested_check_out}
                      onChange={(e) => setFormData({ ...formData, requested_check_out: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Reason</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btnSecondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btnPrimary">
                    Submit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceDetailPage;
